import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  SyntheticEvent
} from 'react';
import { useTheme } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useToggle from 'react-use/lib/useToggle';
import { useTranslation } from 'react-i18next';
import { env } from 'env';
import { useRightSideBar } from '@p81-common/ui';
import type { DrawerStates } from '@p81-common/ui';

export const infoDrawerId = 'infoDrawer';

/**
 * Publishes the fixed top bar's measured height as a custom property on
 * `<html>`, so the content container can offset itself by the real value.
 *
 * The bar's height comes from @p81-common/ui and derives from font metrics
 * (it measures 64.62px, not a round number), so it moves with zoom, text
 * scaling and package updates. Reading it rather than pinning it keeps the
 * gap below the bar stable when it does.
 *
 * The header is rendered by the package, so there is no ref to attach to and
 * it is resolved by selector once mounted. `resolveKey` re-runs that lookup:
 * on /login the app shell is not rendered at all, so without it the header
 * would never be found after signing in and the fallback would stick.
 */
export const useHeaderHeight = (cssVariable: string, resolveKey?: string) => {
  useEffect(() => {
    const header = document.querySelector('header');
    if (!header) return undefined;

    const publish = () => {
      document.documentElement.style.setProperty(
        cssVariable,
        `${header.getBoundingClientRect().height}px`
      );
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(cssVariable);
    };
  }, [cssVariable, resolveKey]);
};

export const useLayoutBehavior = () => {
  const theme = useTheme();
  const {
    setOpen,
    setDrawerLocked,
    open: isRightSidebarOpen
    // drawerLocked
  } = useRightSideBar();

  const [leftDrawerOpenState, setLeftDrawerOpenState] =
    useState<DrawerStates>('closed');
  const MIN_WIDTH_TO_OPEN_LEFT = 1616;
  const isMdUp = useMediaQuery(theme.breakpoints.up(MIN_WIDTH_TO_OPEN_LEFT));
  const isSmUp = useMediaQuery(theme.breakpoints.up('customsm'));

  const handleDrawerChange = () => {
    if (!isSmUp && !isMdUp && leftDrawerOpenState === 'locked') {
      return null;
    }
    if (!isMdUp && leftDrawerOpenState === 'locked') {
      setOpen(false);
      setDrawerLocked(false);
    } else if (!isMdUp && isRightSidebarOpen) {
      setLeftDrawerOpenState('closed');
    }
  };

  useEffect(() => {
    if (isMdUp) {
      setLeftDrawerOpenState('locked');
    } else {
      setLeftDrawerOpenState('closed');
    }
  }, [isMdUp]);

  useEffect(() => {
    if (isRightSidebarOpen && !isMdUp && isSmUp) {
      setLeftDrawerOpenState('closed');
    }
  }, [isRightSidebarOpen, isMdUp, isSmUp]);

  return {
    leftDrawerOpenState,
    setLeftDrawerOpenState,
    handleDrawerChange
  };
};

export const useLayout = () => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [positionAnchor, setPositionAnchor] = useState<object>({ x: 1, y: 1 });

  const { t } = useTranslation(['settings']);

  const handleCloseModal = () => {
    setAnchorEl(null);
    toggleModalOpen();
  };

  const handleSetAnchor = useCallback(
    (elm?: Element | null) => (e: SyntheticEvent) => {
      setPositionAnchor({
        x: e.currentTarget.getBoundingClientRect().right,
        y: e.currentTarget.getBoundingClientRect().top
      });
      setAnchorEl(
        (typeof elm === 'undefined' ? e?.currentTarget : elm) as Element
      );
    },
    []
  );

  const userItems = useMemo(() => {
    const arr = [
      {
        label: t('settings:profile.LOG_OUT'),
        handleClick: toggleModalOpen,
        icon: `${env.REACT_APP_STATIC_ASSETS_URL}/icons2/icon_logout.svg`
      }
    ];
    return arr;
  }, [t, toggleModalOpen]);

  return {
    handleCloseModal,
    anchorEl,
    modalOpen,
    handleSetAnchor,
    userItems,
    positionAnchor
  };
};
